/**
 * MarketplaceDetailModal — modeled on the GPT marketplace detail modal.
 *
 * Layout (top-down):
 *   - Big icon
 *   - Name + author + (optional) official badge
 *   - Tagline / description
 *   - Stats row: rating + install count
 *   - Capabilities (checkmarked bullet list)
 *   - Conversation Starters (chips)
 *   - Ratings histogram (5-star bars)
 *   - "More by <author>" — optional, only when present (we don't fetch
 *     these here in the first cut to keep the modal a single round-trip;
 *     can be added behind a useEffect later)
 *   - Fixed bottom CTA "Deploy to <projectName>"
 *
 * Accessibility:
 *   - role=dialog + aria-modal=true
 *   - focus trapped between the close button and the deploy button
 *   - Esc closes
 *   - Background click closes (with stopPropagation on the modal body)
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import Marketplace from '../../services/marketplace';
import { IonIcon } from '@ionic/react';
import {
  closeOutline,
  checkmarkCircle,
  alertCircleOutline,
  star,
  starOutline,
  downloadOutline,
  cloudDownloadOutline,
} from 'ionicons/icons';
import './MarketplaceDetailModal.css';

const MarketplaceDetailModal = ({
  entry,
  projectId,
  projectName,
  isOpen,
  onClose,
  onDeploy,
  deploying,
}) => {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Integration status — fetched lazily on open so we only spend the
  // round-trip when the modal is actually visible. null = not loaded
  // yet; { required:[], ... } = loaded; error = string when fetch failed.
  // Loading flag drives the skeleton.
  const [intStatus, setIntStatus] = useState(null);
  const [intLoading, setIntLoading] = useState(false);
  const [intError, setIntError] = useState(null);

  useEffect(() => {
    if (!isOpen || !entry?.slug || !projectId) {
      setIntStatus(null);
      setIntError(null);
      return undefined;
    }
    let cancelled = false;
    setIntLoading(true);
    setIntError(null);
    Marketplace.getIntegrationStatus(entry.slug, projectId)
      .then((res) => {
        if (cancelled) return;
        setIntStatus(res || null);
      })
      .catch((err) => {
        if (cancelled) return;
        // Graceful degrade — modal stays usable, deploy stays clickable.
        // The checklist section just won't render.
        setIntError(err?.message || 'Failed to load integration status');
      })
      .finally(() => {
        if (!cancelled) setIntLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, entry?.slug, projectId]);

  // ── Esc to close + focus trap ────────────────────────────────────
  // Lightweight focus trap: Tab/Shift-Tab cycles between the first
  // and last focusable element inside the dialog. Avoids pulling in a
  // dedicated trap-focus library; the focusable set inside this modal
  // is small enough (close button, chips for keyboard activation,
  // deploy button) that this hand-rolled cycle is fine.
  useEffect(() => {
    if (!isOpen) return undefined;
    const node = dialogRef.current;
    if (!node) return undefined;

    const previouslyFocused = document.activeElement;
    // Defer focus until paint so the open animation doesn't yank
    // attention from the user's last action.
    const t = setTimeout(() => {
      closeBtnRef.current?.focus();
    }, 50);

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = node.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, onClose]);

  const handleDeploy = useCallback(() => {
    if (deploying || !entry) return;
    onDeploy?.(entry);
  }, [deploying, entry, onDeploy]);

  if (!isOpen || !entry) return null;

  const histogram = entry.ratingHistogram || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  const histogramTotal = [1, 2, 3, 4, 5].reduce((a, k) => a + (histogram[k] || 0), 0);
  const rating = Number(entry.ratingAvg) || 0;
  const installCount = Number(entry.installCount) || 0;
  const starsFilled = Math.round(rating);

  const deployLabel = projectName
    ? `Deploy to ${projectName}`
    : 'Deploy';

  return (
    <div className="mkt-modal-overlay" onClick={onClose} role="presentation">
      <div
        ref={dialogRef}
        className="mkt-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mkt-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeBtnRef}
          type="button"
          className="mkt-modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          <IonIcon icon={closeOutline} />
        </button>

        <div className="mkt-modal-body">
          <header className="mkt-modal-header">
            {entry.icon ? (
              // eslint-disable-next-line jsx-a11y/img-redundant-alt
              <img src={entry.icon} alt="" className="mkt-modal-icon" />
            ) : (
              <span className="mkt-modal-icon mkt-modal-icon--fallback">
                {(entry.name || entry.slug || '?').slice(0, 1).toUpperCase()}
              </span>
            )}
            <h2 id="mkt-modal-title" className="mkt-modal-title">{entry.name || entry.slug}</h2>
            <p className="mkt-modal-author">
              By {entry.authorName || 'Unknown'}
              {entry.authorType === 'official' && (
                <span className="mkt-modal-author-badge">Official</span>
              )}
            </p>
            <p className="mkt-modal-tagline">
              {entry.description || entry.tagline}
            </p>

            <div className="mkt-modal-stats" aria-label="Stats">
              <div className="mkt-modal-stat">
                <div className="mkt-modal-stat-value">
                  {rating.toFixed(1)}
                  <span className="mkt-modal-stat-stars" aria-hidden="true">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <IonIcon key={n} icon={n <= starsFilled ? star : starOutline} />
                    ))}
                  </span>
                </div>
                <div className="mkt-modal-stat-label">
                  {histogramTotal} {histogramTotal === 1 ? 'rating' : 'ratings'}
                </div>
              </div>
              <div className="mkt-modal-stat">
                <div className="mkt-modal-stat-value">
                  <IonIcon icon={cloudDownloadOutline} aria-hidden="true" />
                  {installCount.toLocaleString()}
                </div>
                <div className="mkt-modal-stat-label">deploys</div>
              </div>
              {entry.category && (
                <div className="mkt-modal-stat">
                  <div className="mkt-modal-stat-value mkt-modal-stat-category">
                    {entry.category}
                  </div>
                  <div className="mkt-modal-stat-label">category</div>
                </div>
              )}
            </div>
          </header>

          {Array.isArray(entry.conversationStarters) && entry.conversationStarters.length > 0 && (
            <section className="mkt-modal-section">
              <h3 className="mkt-modal-section-title">Conversation Starters</h3>
              <ul className="mkt-modal-chips">
                {entry.conversationStarters.slice(0, 6).map((cs, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      className="mkt-modal-chip"
                      // No-op click — these are illustrative, not action
                      // buttons. The keyboard-focusable button shape gives
                      // them tab order in the focus trap without inviting
                      // a click handler we don't have a destination for.
                      tabIndex={0}
                      onClick={() => {}}
                    >
                      {cs}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Integration checklist — only renders once we have data AND
              the listing actually requires one or more integrations. */}
          {intStatus && Array.isArray(intStatus.required) && intStatus.required.length > 0 && (
            <section className="mkt-modal-section">
              <h3 className="mkt-modal-section-title">Required integrations</h3>
              <ul className="mkt-modal-integrations">
                {intStatus.required.map((id) => {
                  const meta = intStatus.integrations?.[id] || { id, name: id, connectPath: '/integrations' };
                  const isConnected = intStatus.connected?.includes(id);
                  return (
                    <li key={id} className={`mkt-modal-integration-row ${isConnected ? 'is-connected' : 'is-missing'}`}>
                      <span className="mkt-modal-integration-status">
                        <IonIcon icon={isConnected ? checkmarkCircle : alertCircleOutline} aria-hidden="true" />
                      </span>
                      <span className="mkt-modal-integration-name">{meta.name}</span>
                      <span className="mkt-modal-integration-state">
                        {isConnected
                          ? 'Connected'
                          : (
                            <a
                              href={meta.connectPath}
                              className="mkt-modal-integration-connect"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Connect after deploy
                            </a>
                          )}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {intStatus.missing && intStatus.missing.length > 0 && (
                <p className="mkt-modal-integration-note">
                  Deploy is allowed; missing integrations can be connected later from Settings → Integrations.
                </p>
              )}
            </section>
          )}
          {intLoading && (
            <section className="mkt-modal-section">
              <h3 className="mkt-modal-section-title">Required integrations</h3>
              <p className="mkt-modal-integration-loading">Checking your connected integrations…</p>
            </section>
          )}
          {intError && (
            <section className="mkt-modal-section">
              <h3 className="mkt-modal-section-title">Required integrations</h3>
              <p className="mkt-modal-integration-error">Couldn’t load integration status. Deploy will still work; you may need to connect integrations from Settings after install.</p>
            </section>
          )}

          {Array.isArray(entry.capabilities) && entry.capabilities.length > 0 && (
            <section className="mkt-modal-section">
              <h3 className="mkt-modal-section-title">Capabilities</h3>
              <ul className="mkt-modal-capabilities">
                {entry.capabilities.map((cap, i) => (
                  <li key={i}>
                    <IonIcon icon={checkmarkCircle} aria-hidden="true" />
                    <span>{cap}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="mkt-modal-section">
            <h3 className="mkt-modal-section-title">Ratings</h3>
            {histogramTotal === 0 ? (
              <p className="mkt-modal-no-ratings">No ratings yet.</p>
            ) : (
              <div className="mkt-modal-histogram" aria-label={`${histogramTotal} ratings`}>
                {[5, 4, 3, 2, 1].map((stars) => {
                  const count = histogram[stars] || 0;
                  const pct = histogramTotal > 0 ? (count / histogramTotal) * 100 : 0;
                  return (
                    <div key={stars} className="mkt-modal-histogram-row">
                      <span className="mkt-modal-histogram-label">
                        {stars}
                        <IonIcon icon={star} aria-hidden="true" />
                      </span>
                      <div className="mkt-modal-histogram-bar">
                        <div
                          className="mkt-modal-histogram-bar-fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="mkt-modal-histogram-count">{count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <footer className="mkt-modal-footer">
          <button
            type="button"
            className="mkt-modal-deploy"
            onClick={handleDeploy}
            disabled={deploying || !projectName}
            aria-label={deployLabel}
          >
            <IonIcon icon={downloadOutline} aria-hidden="true" />
            {deploying ? 'Deploying…' : deployLabel}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default MarketplaceDetailModal;
